import { FormHelperErrorDescription } from '@juvare/common-form-ui/WidgetForm/Components';
import { getVisibleError, handleWidgetUpdate, renderForm, validateCustom } from '@juvare/common-form-ui/WidgetForm/Utils';
import { WidgetHandler } from '@juvare/common-form-ui/WidgetHandler';
import { showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { walkOrganizations } from '@juvare/contact-management/backend/features/permissions/shared';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, CircularProgress, FormControl, IconButton, TextField } from '@mui/material';
import React from 'react';
import { useAlertConfig, useAlertUiTranslation, useUserState } from '../../../../hooks';
import { usePlatformFunction } from '../../../../hooks/usePlatformFunction';
import { SelectRecipientsComponent } from './Components/SelectRecipientsComponent';
import { type } from './types';
export const WidgetRecipients = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const { t } = useAlertUiTranslation();
    const userState = useUserState();
    const { onGetInternalRecipientsSummary } = useAlertConfig();
    let value = props.getData();
    const widgetHandler = new WidgetHandler(props);
    if (value) {
        value = Object.assign({}, value);
    }
    if (value === null || value === void 0 ? void 0 : value.organizations) {
        const userVisibleOrganizationIds = [];
        walkOrganizations(userState.organizations, (organization) => {
            if (value === null || value === void 0 ? void 0 : value.organizations.includes(organization.id)) {
                userVisibleOrganizationIds.push(organization.id);
            }
            return undefined;
        });
        value.organizations = userVisibleOrganizationIds;
    }
    const error = getVisibleError(props);
    const handler = new WidgetHandler(props);
    const setValue = (newValue) => {
        props.setData(newValue);
        renderForm(props, { changed: true, autoSaveImmediately: true });
    };
    const calculateRecipientsResult = usePlatformFunction('calculateRecipients', value
        ? {
            recipients: {
                internal: value,
            },
            calculateCounts: true,
            channels: (_a = widgetHandler.formData) === null || _a === void 0 ? void 0 : _a.channels,
        }
        : undefined);
    const getDefaultRecipientsSummary = (counts) => {
        const groupText = counts.groupCount > 0 ? t('common.groupsCount', { count: counts.groupCount }) : '';
        const organizationText = counts.organizationCount > 0 ? t('common.organizationsCount', { count: counts.organizationCount }) : '';
        const accountText = counts.accountCount > 0 ? t('common.accountsCount', { count: counts.accountCount }) : '';
        return [groupText, organizationText, accountText].filter((text) => !!text).join(', ');
    };
    const counts = ((_c = (_b = calculateRecipientsResult.data) === null || _b === void 0 ? void 0 : _b.result) === null || _c === void 0 ? void 0 : _c.counts)
        ? Object.assign(Object.assign({}, (_e = (_d = calculateRecipientsResult.data) === null || _d === void 0 ? void 0 : _d.result) === null || _e === void 0 ? void 0 : _e.counts), { ccCount: 0 }) : undefined;
    const valueString = counts && value
        ? onGetInternalRecipientsSummary
            ? onGetInternalRecipientsSummary(counts, value)
            : getDefaultRecipientsSummary(counts)
        : undefined;
    const handleRecipientSelection = () => {
        showCustomDialog({
            fullScreen: true,
            content: (close, ariaLabelledBy) => (React.createElement(SelectRecipientsComponent, { handleClose: close, setValue: setValue, value: value, ariaLabelledBy: ariaLabelledBy })),
        });
    };
    const handleRemoveRecipients = () => {
        props.setData(undefined);
        props.rootUpdateAll();
        handler.setShowErrorsOverride('all');
        renderForm(props, { changed: true, autoSaveImmediately: true });
    };
    return (React.createElement(FormControl, { variant: 'outlined', fullWidth: true, required: (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.required, error: !!error },
        React.createElement(Box, { display: 'flex', alignItems: 'center' },
            React.createElement(TextField, { "data-testid": 'alert-recipients', size: 'medium', label: t('wizard.addRecipients.internalRecipients'), type: 'text', fullWidth: true, disabled: ((_g = props.config) === null || _g === void 0 ? void 0 : _g.disabled) || props.config.followUp != null, color: 'primary', variant: 'outlined', error: !!error, value: valueString || '', inputProps: { 'data-testid': 'alert-recipients', 'aria-label': 'Internal recipient selection' }, autoComplete: 'off', InputLabelProps: { shrink: valueString !== undefined }, InputProps: {
                    readOnly: true,
                    endAdornment: (value === null || value === void 0 ? void 0 : value.accountsFilter) && calculateRecipientsResult.isLoading ? React.createElement(CircularProgress, null) : undefined,
                }, sx: { backgroundColor: 'background.paper' }, onClick: handleRecipientSelection }),
            valueString === undefined ? (React.createElement(IconButton, { color: 'primary', "aria-label": t('wizard.addRecipients.addInternalRecipients'), role: 'button', disabled: ((_h = props.config) === null || _h === void 0 ? void 0 : _h.disabled) || props.config.followUp != null, sx: { p: 1, ml: 2 }, "data-testid": 'add-recipients', onClick: handleRecipientSelection },
                React.createElement(AddCircleOutlineIcon, { fontSize: 'medium' }))) : (React.createElement(IconButton, { color: 'primary', "aria-label": t('wizard.addRecipients.removeInternalRecipients'), role: 'button', disabled: ((_j = props.config) === null || _j === void 0 ? void 0 : _j.disabled) || props.config.followUp != null, sx: { p: 1, ml: 2 }, "data-testid": 'remove-recipients', onClick: handleRemoveRecipients },
                React.createElement(RemoveCircleOutlineIcon, { fontSize: 'medium' })))),
        React.createElement(FormHelperErrorDescription, { error: error })));
};
export const WidgetRecipientsEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props),
        React.createElement(WidgetRecipients, Object.assign({}, props))));
};
export const widgetRecipients = {
    type,
    createWidget: () => ({ type, key: 'recipients', label: 'Recipients' }),
    validate: (props) => {
        const value = props.getData();
        return validateCustom(props, value);
    },
    icon: React.createElement(PersonAddAltIcon, null),
    editorComponent: WidgetRecipientsEditor,
    update: handleWidgetUpdate,
    component: WidgetRecipients,
};
